/* eslint-disable import/first */
/* eslint-disable global-require */
import cookie from 'cookie';
import Immutable from 'immutable';
import Iso from 'iso';
import _ from 'lodash';
import { addLocaleData } from 'react-intl';
import enLocaleData from 'react-intl/locale-data/en';
import esLocaleData from 'react-intl/locale-data/es';
import frLocaleData from 'react-intl/locale-data/fr';
import jaLocaleData from 'react-intl/locale-data/ja';
import ruLocaleData from 'react-intl/locale-data/ru';
import zhLocaleData from 'react-intl/locale-data/zh';
import filLocaleData from 'react-intl/locale-data/fil';
import viLocaleData from 'react-intl/locale-data/vi';
import { Provider } from 'react-redux';
import { match, RouterContext, Router, browserHistory } from 'react-router';
import { createStore, applyMiddleware, compose } from 'redux';
import { combineReducers } from 'redux-immutablejs';
import { createEpicMiddleware, combineEpics } from 'redux-observable';
import thunk from 'redux-thunk';
import httpClient from 'superagent';
import when from 'when';
import node from 'when/node';
import AppActions from './actions/AppActions';
import ApiClient from './api/ApiClient';
import airbrakeMiddleware from './reduxMiddlewares/airbrakeMiddleware';
import apiRequestMiddleware from './reduxMiddlewares/apiRequestMiddleware';
import authMiddleware from './reduxMiddlewares/authMiddleware';
import redirectMiddleware from './reduxMiddlewares/redirectMiddleware';
import transitionMiddleware from './reduxMiddlewares/transitionMiddleware';
import gaMiddleware from './reduxMiddlewares/gaMiddleware';
import analyticsMiddleware from './reduxMiddlewares/analyticsMiddleware';
import routes from './routes/Routes';
import ConsoleErrorService from '@bibliocommons/utils-console';
import AirbrakeErrorService from '@bibliocommons/utils-airbrake';
import RoutingUtils from '@bibliocommons/utils-routing';
import BCGoogleAnalyticsUtils from '@bibliocommons/utils-google-analytics';
import ErrorActions from './actions/ErrorActions';
import { currentLibrarySelector } from './selectors/LibrarySelector';
import { currentAccountSelector, currentUserSelector } from './selectors/AuthSelector';

const React = require('react');
const ReactDOM = require('react-dom');
const ReactDOMServer = require('react-dom/server');
const Helmet = require('react-helmet');

if (!global.Intl) global.Intl = require('intl');

const BOOTSTRAP_DATA_WHITE_LIST = [
  'airbrakeConfig',
  'apiGatewayURLForClient',
  'discloseErrors',
  'enableReduxDevTools',
  'gaTrackingId',
  'gtmContainerId',
  'gtmContainerAuth',
  'gtmContainerPreview',
  'head',
  'libraryDomain',
  'reduxStoreData',
  'routeName'
];

const whitelist = obj => _.pick(obj, ...BOOTSTRAP_DATA_WHITE_LIST);

/**
 * @param  {object} initialState - initial state (@see redux.createStore(), second argument)
 * @param  {object} apiClient - we inject the httpClient for easier mocking
 *                            plus easily changing http library on client and server.
 *                            (note - this will change with webpack build)
 * @param  {boolean} enableWebpackHMR - whether to enable hot module reloading (@see config)
 *
 * @return {object} - redux stored with all the initial data loaded
 */
function createReduxStore(initialState, apiClient, enableWebpackHMR = false, enableReduxDevTools = false, errorService) {
  let rootEpic = (...args) => combineEpics(..._.values(require('./epics')))(...args, apiClient);
  const epicMiddleware = createEpicMiddleware(rootEpic);
  const middleware = [
    authMiddleware,
    apiRequestMiddleware(apiClient),
    epicMiddleware,
    redirectMiddleware,
    thunk.withExtraArgument(apiClient),
    transitionMiddleware,
    airbrakeMiddleware(errorService),
    gaMiddleware(BCGoogleAnalyticsUtils),
    analyticsMiddleware()
  ];
  let combinedReducers = combineReducers(require('./reducers').default);
  const lastCreatedStore = createStore(combinedReducers, initialState, compose(
    applyMiddleware(..._.compact(middleware)),
    enableReduxDevTools && window.devToolsExtension ? window.devToolsExtension() : f => f
  ));

  if (enableWebpackHMR && module.hot) {
    module.hot.accept('./reducers', () => {
      combinedReducers = combineReducers(require('./reducers').default);
      rootEpic = combineEpics(..._.values(require('./epics')));
      lastCreatedStore.replaceReducer(combinedReducers);
      epicMiddleware.replaceEpic(rootEpic);
    });
  }

  return lastCreatedStore;
}

/**
 * Initializes a handler for uncaught errors (browser only).
 * @private
 * @param {object}  isoState - The deserialized iso-state object
 * @param {boolean} isoState.discloseErrors
 * @param {object}  isoState.airbrakeConfig
 */
function clientHandleErrors(errorService, reduxStore) {
  global.onerror = (message, fileName, lineNumber, columnNumber, error) => {
    const err = error || { error: { message, fileName, lineNumber, columnNumber } };
    errorService
      .notify(err)
      .catch(e => console.error(e)) // eslint-disable-line no-console
      .then(notice => {
        reduxStore.dispatch(ErrorActions.storeInternalError(notice));
      }); // eslint-disable-line no-console
  };
}

/**
 * Renders React on client. This needs to be called first time when React gets mounted on browser.
 *
 * @return {undefined}
 */
function renderOnClient() {
  function logPageView() {
    BCGoogleAnalyticsUtils.pageView(`${window.location.pathname}${window.location.search}`);
  }

  const rootElement = document.getElementById('react_root');

  Iso.bootstrap((clientHydrationData) => {
    // Note: isoState comes from <div class="___iso-state___"> in index.html.
    // See iso.add() and BOOTSTRAP_DATA_WHITE_LIST.
    const { apiGatewayURLForClient,
            enableWebpackHMR,
            libraryDomain,
            reduxStoreData,
            enableReduxDevTools,
            gaTrackingId,
            airbrakeConfig } = clientHydrationData;

    const errorService = airbrakeConfig.enabled ?
      new AirbrakeErrorService(airbrakeConfig) :
      new ConsoleErrorService();

    const apiClient = new ApiClient(libraryDomain, apiGatewayURLForClient, httpClient);
    const reduxStore = createReduxStore(Immutable.fromJS(reduxStoreData), apiClient, enableWebpackHMR, enableReduxDevTools, errorService);

    clientHandleErrors(errorService, reduxStore);

    reduxStore.dispatch(AppActions.fetchCookies());

    addLocaleData([...enLocaleData, ...frLocaleData, ...esLocaleData,
      ...jaLocaleData, ...ruLocaleData, ...zhLocaleData, ...filLocaleData, ...viLocaleData]);

    // Creates and initialize GA tracking
    const currentLibrary = currentLibrarySelector(reduxStore.getState());
    BCGoogleAnalyticsUtils.initialize(gaTrackingId, currentLibrary.toJS(), reduxStoreData.localization.currentLanguage);

    ReactDOM.render(
      /* eslint-disable react/jsx-filename-extension */
      <Provider store={reduxStore}>
        <Router
          history={browserHistory}
          routes={routes(reduxStoreData.app.basePath)}
          onUpdate={logPageView} />
      </Provider>, rootElement);
      /* eslint-enable react/jsx-filename-extension */
  });
}

/**
 * Renders React on server. This can be disabled by passing disableSSR to be true.
 *
 * @param  {object} httpClientForServer - http client like superagent which works on server
 * @param  {boolean} disableSSR - check if the SSR is switched off
 * @param  {object} bootstrapData - This is the data we will need to send down to client.
 * @param  {Function} callback - This is the express callback hook which gets on success or errorService
 *                    //TODO need to make it more express way callback
 * @return {[type]}
 */
function renderOnServer({ disableSSR, bootstrapData }) {
  let apiClient;
  let reduxStore;

  return when.resolve()
  .then(() => {
    const { apiGatewayURLForServer,
            currentUrl,
            enableWebpackHMR,
            libraryDomain,
            requestHeaders } = bootstrapData;

    const appConfig = _.pick(bootstrapData, ['featureFlags', 'basePath', 'allowFeatureFlagEditing', 'appVersion', 'coreAssets', 'localBranch', 'mobileApp']);

    // Create the store and dispatch an inital set of config properties to it
    apiClient = new ApiClient(libraryDomain, apiGatewayURLForServer, httpClient, requestHeaders);
    reduxStore = createReduxStore(Immutable.Map(), apiClient, enableWebpackHMR);

    reduxStore.dispatch(AppActions.loadAppConfig(appConfig));
    reduxStore.dispatch(AppActions.loadCookies(cookie.parse(requestHeaders.cookie || '')));

    return node.call(match, { routes: routes(appConfig.basePath), location: currentUrl });
  })
  .then(([redirectLocation, renderProps]) => // eslint-disable-line no-unused-vars
    RoutingUtils
    .fetchDataForCurrentRoute(renderProps, reduxStore, apiClient)
    .then(() => {
      const isFrbr = reduxStore.getState().getIn(['search', 'catalogSearch', 'frbr']);
      const routePaths = _.compact(_.map(renderProps.routes, 'path'));
      const routeName = isFrbr ? ['frbr'].concat(routePaths) : routePaths;
      const router = renderProps.router;

      const currentUser = currentUserSelector(reduxStore.getState());
      const currentAccount = currentAccountSelector(reduxStore.getState());

      const anyRouteRequiresAuthentication = _.some(renderProps.components, (component) => component.requiresAuthentication);
      const anyRouteRequiresAccount = _.some(renderProps.components, (component) => component.requiresAccount);

      if ((anyRouteRequiresAuthentication && !currentUser) || (anyRouteRequiresAccount && !currentAccount)) {
        return {
          redirect: currentUser ?
            '/user_dashboard' :
            `/user/login?destination=${encodeURIComponent(`${router.location.pathname}${router.location.search}`)}`
        };
      }

      const rootComponentHTML = disableSSR ?
                                '<div></div>' :
                                ReactDOMServer.renderToString(
                                  <Provider store={reduxStore}>
                                    <RouterContext {...renderProps} />
                                  </Provider>
                                );

      const clientHydrationData = whitelist({
        ...bootstrapData,
        head: Helmet.rewind(),
        routeName,
        reduxStoreData: reduxStore.getState().toJS()
      });

      return {
        clientHydrationData,
        rootComponentHTML: (new Iso()).add(rootComponentHTML, clientHydrationData).render()
      };
    })
  );
}

const application = {
  renderOnClient,
  renderOnServer
};

if (typeof __CLIENT__ !== 'undefined' && __CLIENT__) {
  window.onpageshow = function handleSafariBFCache(event) {
    if (event.persisted && typeof window.parentIFrame === 'undefined') {
      window.location.href += '&reload=true';
    }
  };

  application.renderOnClient();
}

export default application;



// WEBPACK FOOTER //
// ./app/application.js